import { type ComponentProps, useCallback, useRef, useState } from 'react'
import {
  type AutocompleteResponse,
  type AutocompleteSuggestion,
} from '@/features/addressAutocomplete/api/autocomplete/autocomplete'
import { type PlaceDetailsResponse } from '@/features/addressAutocomplete/api/details/details'
import type { ErrorResponse } from '@/features/addressAutocomplete/api/types'
import { logger } from '@/services/logger/logger.client'
import { AutoComplete } from 'ui/src/components/AutoComplete/AutoComplete'

export type AddressResult = PlaceDetailsResponse

type Props = Omit<ComponentProps<typeof AutoComplete>, 'getOptions' | 'onSelect' | 'itemToString'> & {
  onAddressSelect?: (address: AddressResult) => void
}

export function AddressAutoCompleteField({ onAddressSelect, ...forwardedProps }: Props) {
  // Session token groups autocomplete requests with the final place details
  // call so Google Places (New) bills them together as a single session.
  const sessionTokenRef = useRef<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const getOptions = useCallback(async (input: string) => {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = crypto.randomUUID()
    }

    abortControllerRef.current?.abort()
    abortControllerRef.current = new AbortController()

    try {
      setErrorMessage('')

      const response = await fetch('/api/places/autocomplete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: abortControllerRef.current.signal,
        body: JSON.stringify({ query: input, sessionToken: sessionTokenRef.current }),
      })

      if (!response.ok) {
        const data = (await response.json()) as ErrorResponse
        logger.error('AddressAutoCompleteField | failed to fetch autocomplete suggestions', { data })
        return []
      }

      const data = (await response.json()) as AutocompleteResponse
      return data.suggestions ?? []
    } catch (error) {
      // We swallow abort errors as they are expected when the user cancels the request
      if (error instanceof Error && error.name === 'AbortError') {
        return []
      }

      logger.error('AddressAutoCompleteField | failed to fetch autocomplete suggestions', { error })
      return []
    }
  }, [])

  const handleSelect = useCallback(
    async (item: AutocompleteSuggestion) => {
      try {
        setIsLoading(true)

        const response = await fetch('/api/places/details', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ placeId: item.placeId, sessionToken: sessionTokenRef.current }),
        })

        if (!response.ok) {
          const data = (await response.json()) as ErrorResponse
          logger.error('AddressAutoCompleteField | failed to fetch place details', { data })
          return
        }

        const address = (await response.json()) as PlaceDetailsResponse
        onAddressSelect?.(address)
      } catch (error) {
        logger.error('AddressAutoCompleteField | failed to fetch place details', { error })
      } finally {
        sessionTokenRef.current = crypto.randomUUID()
        setIsLoading(false)
      }
    },
    [onAddressSelect],
  )

  return (
    <AutoComplete
      getOptions={getOptions}
      onSelectItem={handleSelect}
      itemToString={(item) => {
        // Strips trailing city segment (e.g. ", Bandhagen") if any
        return item.text.replace(/, [^,]+$/, '')
      }}
      itemToOption={(item) => item.text}
      minCharactersForSuggestions={2}
      debounceDelayMs={400}
      loading={isLoading}
      message={errorMessage}
      {...forwardedProps}
    />
  )
}
